import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { SEOHead } from "@/components/SEOHead";
import { 
  Store, 
  ArrowLeft, 
  TrendingUp,
  BarChart3,
  Package,
  Shield
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

const SellerLogin = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn, signOut } = useAuth();

  if (!isSupabaseConfigured) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-center">Seller Login Unavailable</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-center">
              Supabase authentication is not configured. Please check your environment variables.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const form = new FormData(e.currentTarget);
      const email = String(form.get("email") || "").trim();
      const password = String(form.get("password") || "");

      if (!email || !password) {
        toast.error("Please fill in all fields");
        setLoading(false);
        return;
      }

      console.log("Attempting seller login for:", email);

      const { data, error } = await signIn(email, password);

      if (error) {
        console.error("Seller login error:", error);
        toast.error(error.message || "Login failed");
        setLoading(false);
        return;
      }

      console.log("Seller login successful:", data);

      // Check if user is a seller
      const userRole = data.user?.user_metadata?.role;
      if (userRole !== 'seller') {
        toast.error("This account is not registered as a seller. Please use the regular login page.");
        await signOut();
        setLoading(false);
        return;
      }

      toast.success("Welcome back to your seller dashboard!");
      // Always redirect sellers to dashboard
      navigate("/seller");

    } catch (err: any) {
      console.error("Unexpected seller login error:", err);
      toast.error("An unexpected error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: TrendingUp,
      title: "Sales Analytics",
      description: "Track your revenue and growth"
    },
    {
      icon: Package,
      title: "Inventory Management",
      description: "Manage products and stock levels"
    },
    {
      icon: BarChart3,
      title: "Performance Insights",
      description: "Understand your customers better"
    }
  ];

  return (
    <>
      <SEOHead
        title="Seller Login - Bahama Marketplace"
        description="Sign in to your Bahama Marketplace seller dashboard. Manage your store, products, and orders."
      />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5">
        {/* Header */}
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm text-muted-foreground">Back to Bahama Marketplace</span>
            </Link>
            <Link to="/seller/register" className="text-sm text-primary hover:underline">
              New seller? Get started
            </Link>
          </div>
        </div>

        <div className="container mx-auto px-4 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left Side - Branding */}
            <div className="space-y-8 text-center lg:text-left">
              <div>
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <Store className="h-4 w-4" />
                  Seller Dashboard
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                  Welcome back to your <span className="text-primary">Bahama seller dashboard</span>
                </h1>
                <p className="text-xl text-muted-foreground mb-8">
                  Manage your Bahama store, track sales, and grow your business with powerful seller tools.
                </p>
              </div>

              <div className="grid gap-6">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-card border border-border/40">
                      <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{feature.title}</h3>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-card border border-border/40 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-green-500" />
                  <h3 className="font-semibold">Secure & Trusted</h3>
                </div>
                <p className="text-muted-foreground text-sm">
                  Your seller account is protected with enterprise-grade security. 
                  Your data and your customers' information are always safe.
                </p>
              </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="max-w-md mx-auto w-full">
              <Card className="shadow-xl border-border/40">
                <CardHeader>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Store className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-2xl">Sign in to your Bahama seller account</CardTitle>
                    <CardDescription>
                      Access your seller dashboard and manage your store
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="relative">
                    <Separator />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="bg-background px-2 text-xs text-muted-foreground">
                        Sign in with your email and password
                      </span>
                    </div>
                  </div>

                  {/* Login Form */}
                  <form onSubmit={onSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        required
                        disabled={loading}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                          Forgot password?
                        </Link>
                      </div>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        placeholder="••••••••"
                        required
                        disabled={loading}
                        className="mt-1"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={loading}
                      className="w-full"
                      size="lg"
                    >
                      {loading ? "Signing in..." : "Sign In to Dashboard"}
                    </Button>
                  </form>

                  <div className="text-center space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Don't have a seller account?
                    </p>
                    <Link to="/seller/register" className="text-sm text-primary hover:underline font-medium">
                      Become a seller today
                    </Link>
                  </div>

                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      This is the dedicated seller login. If you're a customer, please use the regular login page.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SellerLogin;